import React, { Component } from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import StaticIcon from '../StaticIcon';

const Wrapper = styled.div`
  display: inline-block;
  width: ${(props) => (props.fullWidth ? '100%' : 'auto')};
  min-width: 185px;
  color: ${(props) =>
    props.white ? props.theme.colors.palette.white : 'inherit'};
  vertical-align: top;
`;

const Label = styled.div`
  font-size: 10px;
  font-weight: normal;
  line-height: 21px;
  text-transform: uppercase;
  color: ${(props) =>
    props.white ? props.theme.colors.palette.white : '#7d7d7d'};
  transition: 0.2s ease transform, 0.2s ease font-size;
  cursor: text;
`;

const Caret = styled(StaticIcon)`
  position: absolute;
  right: 0;
  bottom: 15px;
  color: ${(props) =>
    props.white
      ? props.theme.colors.palette.white
      : props.theme.colors.palette.black};
`;

const LabelWrapper = styled.label`
  display: flex;
  flex-direction: column-reverse;
  position: relative;
`;

function getStyleColor(props) {
  if (props.error) {
    return '#e35d6d';
  }
  if (props.white) {
    return props.theme.colors.palette.white;
  }
  return props.theme.colors.palette.black;
}

const SelectElement = styled.select`
  -webkit-appearance: none;
  appearance: none;
  outline: 0;
  background-color: transparent;
  border: 0;
  border-radius: 0;
  border-bottom: 2px solid ${(props) => getStyleColor(props)};
  color: ${(props) =>
    props.white
      ? props.theme.colors.palette.white
      : props.theme.colors.palette.black};
  padding: 0 30px 8px 0;
  width: 100%;
  font-family: inherit;
  font-size: 14px;
  font-weight: normal;
  line-height: 21px;
  position: relative;
  z-index: 2;

  @media (max-width: 1024px) {
    /* Necessary to prevent browsers like Mobile Safari from zooming in on text
       fields when font-size is less than 16px. */
    font-size: 16px;
  }

  ~ ${Caret} {
    color: ${(props) => getStyleColor(props)};
  }

  &:focus {
    border-color: ${(props) => props.theme.focusColor || '#7c89d3'};

    ~ ${Label}, ~ ${Caret} {
      /* Color the label and icon too. */
      color: ${(props) => props.theme.focusColor || '#7c89d3'};
    }
  }

  option {
    color: ${({ theme }) => theme.colors.palette.black};
  }

  option[disabled] {
    display: none;
  }
`;

const Footer = styled.div`
  min-height: ${(props) => (props.hint ? 30 : 15)}px;
  margin: 4px 0 0 0;
  font-size: 10px;
  line-height: 1.5;
  color: ${(props) =>
    props.white ? props.theme.colors.palette.white : 'inherit'};
`;

const Hint = styled.p`
  margin: 0;
`;

const Error = styled.p`
  margin: 0;
  color: ${(props) => props.theme.errorColor || '#e35d6d'};
`;

export default class Select extends Component {
  static Label = Label;
  static SelectElement = SelectElement;
  static Caret = Caret;
  static Footer = Footer;
  static Hint = Hint;
  static Error = Error;

  static propTypes = {
    /**
     * Optional class name for styling
     */
    className: PropTypes.string,
    /**
     * Error message or flag
     */
    error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    /**
     * Make input white
     */
    fullWidth: PropTypes.bool,
    /**
     * Optional hint message
     */
    hint: PropTypes.string,
    /**
     * ID for the select element
     */
    id: PropTypes.string,
    /**
     * Optional label for select
     */
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    /**
     * onchange callback
     */
    onChange: PropTypes.func,
    /**
     * Callback triggered on mount
     */
    onMount: PropTypes.func,
    /**
     * Array of options [{ name: "Name", value: "value" }]
     */
    options: PropTypes.array.isRequired,
    /**
     * Optional placeholder
     */
    placeholder: PropTypes.string,
    /**
     * Current value
     */
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    /**
     * Make input white
     */
    white: PropTypes.bool,
  };

  componentDidMount() {
    const { onMount } = this.props;

    onMount && onMount(this.input);
  }

  render() {
    const {
      className,
      error,
      hint,
      label,
      onChange,
      options,
      placeholder,
      white,
      value,
      id,
      fullWidth,
      ...props
    } = this.props;

    return (
      <Wrapper className={className} fullWidth={fullWidth}>
        <LabelWrapper htmlFor={id}>
          <SelectElement
            value={value}
            onChange={onChange}
            id={id}
            white={white}
            {...props}
            ref={(input) => (this.input = input)}
            error={error}
          >
            {placeholder ? (
              <option value="" disabled>
                {placeholder}
              </option>
            ) : null}

            {options.map((option) => (
              <option value={option.value} key={option.value}>
                {option.name}
              </option>
            ))}
          </SelectElement>
          {label ? <Label white={white}>{label}</Label> : null}
          <Caret size={8} type="caret" onClick={this.onClick} />
        </LabelWrapper>
        <Footer error={error} white={white} hint={hint}>
          {hint ? <Hint>{hint}</Hint> : null}
          {error ? <Error>{error}</Error> : null}
        </Footer>
      </Wrapper>
    );
  }
}
